import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { parseErrorMessage } from "@/lib/queryClient";
import { FINANCIAL_FIELDS, type FinancialDatum, type FinancialField } from "@shared/member-financials";
import { useMemberFinancials } from "./use-member-financials";
import { FinancialThresholdSettings } from "./financial-threshold-settings";

const labels: Record<FinancialField, string> = {
  financial_status: "Financial status",
  balance: "Balance (credit-positive)",
  current_dues: "Current dues",
  on_time_payment_rate: "On-time payment percent",
  last_payment: "Last payment",
};

const observationDateFormat = new Intl.DateTimeFormat("en-US", {
  month: "short", day: "numeric", year: "numeric",
  hour: "numeric", minute: "2-digit", timeZoneName: "short",
});

export function FinancialEvidence({ datum, compact = false }: { datum?: FinancialDatum; compact?: boolean }) {
  const display = datum?.state === "observed" || datum?.state === "stale"
    ? datum.display : datum?.display === "Temporarily unavailable" ? datum.display : "Unknown";
  const observedDate = datum?.observedAt ? new Date(datum.observedAt) : null;
  const readableDate = observedDate && Number.isFinite(observedDate.getTime())
    ? observationDateFormat.format(observedDate) : null;
  if (compact && datum?.state !== "stale") return (
    <span className="text-sm font-medium text-gray-900 tabular-nums" title={datum?.reason}>{display}</span>
  );
  const reason = datum?.reason || "No approved source observation is available.";
  const warnings = [
    reason.includes("Person-level balance") ? "Person-level balance" : null,
    reason.includes("currency unverified") ? "Currency unverified" : null,
  ].filter(Boolean);
  return (
    <div>
      <span className="text-sm font-medium text-gray-900 tabular-nums">{display}</span>
      {datum?.state === "stale" && <p className="text-xs text-amber-700">Stale · last known value</p>}
      {warnings.length > 0 && <p className="text-xs text-amber-700 mt-1">{warnings.join(" · ")}</p>}
      {readableDate && datum?.observedAt && (
        <p className="text-xs text-gray-500 mt-1">
          As of <time dateTime={datum.observedAt}>{readableDate}</time>
        </p>
      )}
      {!compact && <details className="mt-1 text-xs text-gray-500">
        <summary className="cursor-pointer w-fit rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
          Details
        </summary>
        <p className="mt-1 break-words">{reason}</p>
        {datum && datum.state !== "unknown" && <p className="mt-1 capitalize">Evidence: {datum.state}</p>}
      </details>}
    </div>
  );
}

export function FinancialObservationPanel({ memberId, propertyId }: { memberId: number; propertyId?: number | null }) {
  const observations = useMemberFinancials([memberId]);
  const member = observations.members.get(memberId);
  return (
    <section className="space-y-4" aria-label="Financial source observations">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Source observations</h3>
        <FinancialThresholdSettings initialPropertyId={propertyId ?? 0} />
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <p className="font-semibold">Not live · Automated messaging off</p>
        <p className="mt-1">Values reflect the last source read. Unknown does not mean zero.</p>
        <details className="mt-2">
          <summary className="cursor-pointer w-fit">Threshold preview details</summary>
          <p className="mt-1">Threshold preview: {member?.classification === "unknown" || !member
            ? "Unknown — no eligible balance evidence"
            : member.classification === "zero-band" ? "Within zero band" : `${member.classification} balance`}. No message will be sent.</p>
          {member?.automation.reason && <p className="mt-1">{member.automation.reason}</p>}
        </details>
      </div>
      {observations.error && <p role="alert" className="text-sm text-red-700">{parseErrorMessage(observations.error, "Unable to load financial evidence. Please retry.")}</p>}
      {observations.isPending ? (
        <div aria-label="Loading source observations" className="space-y-3">
          {FINANCIAL_FIELDS.map(field => <Skeleton key={field} className="h-12 w-full" />)}
        </div>
      ) : (
        <dl className="divide-y divide-gray-100">
          {(["financial_status", "balance", "current_dues", "on_time_payment_rate", "last_payment"] as FinancialField[]).map(field => (
            <div key={field} className="py-3 first:pt-0">
              <dt className="text-xs text-gray-500 mb-1">{labels[field]}</dt>
              <dd><FinancialEvidence datum={member?.fields[field]} /></dd>
            </div>
          ))}
        </dl>
      )}
      <Button variant="outline" size="sm" disabled={observations.isFetching} onClick={() => void observations.reload()}>
        {observations.isFetching ? "Reloading…" : "Reload observations"}
      </Button>
    </section>
  );
}
